
import React from 'react';
import { CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Step {
  id: string;
  label: string;
}

interface CampaignStepsProps {
  steps: Step[];
  activeStep: string;
  isStepComplete: (step: string) => boolean;
  isStepNavigable: (step: string) => boolean;
  setActiveStep: (step: string) => void;
}

const CampaignSteps: React.FC<CampaignStepsProps> = ({
  steps,
  activeStep,
  isStepComplete,
  isStepNavigable,
  setActiveStep
}) => {
  return (
    <div className="mt-6">
      <div className="hidden md:flex items-center">
        {steps.map((step, index) => (
          <React.Fragment key={step.id}>
            {/* Step */}
            <div className="flex flex-col items-center">
              <button
                onClick={() => isStepNavigable(step.id) && setActiveStep(step.id)}
                className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center mb-2 border transition-colors",
                  activeStep === step.id
                    ? "bg-brand-blue text-white border-brand-blue [&_svg]:text-white"
                    : isStepComplete(step.id)
                    ? "bg-brand-blue text-white border-brand-blue hover:bg-brand-blue/90"
                    : isStepNavigable(step.id)
                    ? "bg-muted border-muted-foreground/20 text-muted-foreground hover:bg-muted/80 cursor-pointer"
                    : "bg-muted border-muted-foreground/20 text-muted-foreground cursor-not-allowed opacity-50"
                )}
                disabled={!isStepNavigable(step.id)}
              >
                {isStepComplete(step.id) && activeStep !== step.id ? (
                  <CheckCircle2 className="h-5 w-5 text-white" />
                ) : (
                  <span className={
                    activeStep === step.id || isStepComplete(step.id) 
                      ? "text-white" 
                      : "text-muted-foreground"
                  }>
                    {index + 1}
                  </span>
                )}
              </button>
              <span
                className={cn(
                  "text-sm font-medium",
                  activeStep === step.id
                    ? "text-primary"
                    : "text-muted-foreground"
                )}
              >
                {step.label}
              </span>
            </div>
            
            {/* Connector line */}
            {index < steps.length - 1 && (
              <div
                className={cn(
                  "h-0.5 flex-1 mx-2",
                  isStepComplete(step.id) && isStepComplete(steps[index + 1].id)
                    ? "bg-primary"
                    : isStepComplete(step.id)
                    ? "bg-gradient-to-r from-primary to-muted"
                    : "bg-muted"
                )}
              />
            )}
          </React.Fragment>
        ))}
      </div>
      
      {/* Mobile view - dropdown */}
      <div className="md:hidden">
        <select
          className="w-full border border-border rounded-md p-2 bg-background"
          value={activeStep}
          onChange={(e) => isStepNavigable(e.target.value) && setActiveStep(e.target.value)}
        >
          {steps.map((step, index) => (
            <option key={step.id} value={step.id} disabled={!isStepNavigable(step.id)}>
              {index + 1}. {step.label} {isStepComplete(step.id) && "✓"}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default CampaignSteps;
